/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowRight,
  FiCalendar,
  FiEdit2,
  FiGlobe,
  FiX,
} from 'react-icons/fi';
import AppAlert from '../../../../shared/components/app-alert/AppAlert';
import { getEsimPlansApi } from '../../api/esimApi';
import {
  ESIM_ROUTES,
  ESIM_SORT_TABS,
  ESIM_DATA_BUCKETS,
  ESIM_VALIDITY_BUCKETS,
} from '../../constants/esimConstants';
import {
  parseEsimSearchQuery,
  buildEsimSearchQuery,
  dataCapacityInGb,
  isUnlimitedPlan,
  tripDurationInDays,
} from '../../utils/esimUtils';
import EsimPlanCard from '../../components/EsimPlanCard/EsimPlanCard';
import EsimPlanFilters from '../../components/EsimPlanFilters/EsimPlanFilters';
import EsimPlanDetailsModal from '../../components/EsimPlanDetailsModal/EsimPlanDetailsModal';
import EsimFareSummary from '../../components/EsimFareSummary/EsimFareSummary';
import EsimSearchForm from '../../components/EsimSearchForm/EsimSearchForm';
import './EsimPlansPage.scss';

const EMPTY_FILTERS = { dataBuckets: [], validityBuckets: [] };

const matchesDataBuckets = (plan, keys) => {
  if (!keys || keys.length === 0) return true;
  return keys.some((key) => {
    const bucket = ESIM_DATA_BUCKETS.find((b) => b.key === key);
    if (!bucket) return false;
    if (bucket.key === 'unlimited') return isUnlimitedPlan(plan);
    const gb = dataCapacityInGb(plan);
    return gb > bucket.min && gb <= bucket.max;
  });
};

const matchesValidityBuckets = (plan, keys) => {
  if (!keys || keys.length === 0) return true;
  return keys.some((key) => {
    const bucket = ESIM_VALIDITY_BUCKETS.find((b) => b.key === key);
    if (!bucket) return false;
    const v = Number(plan?.validity || 0);
    return v > bucket.min && v <= bucket.max;
  });
};

const sortPlans = (plans, sortKey) => {
  const list = [...plans];
  switch (sortKey) {
    case 'price-desc':
      return list.sort(
        (a, b) =>
          Number(b.totalPrice || b.netPrice || 0) -
          Number(a.totalPrice || a.netPrice || 0),
      );
    case 'data-desc':
      return list.sort((a, b) => dataCapacityInGb(b) - dataCapacityInGb(a));
    case 'validity-desc':
      return list.sort(
        (a, b) => Number(b.validity || 0) - Number(a.validity || 0),
      );
    case 'price-asc':
    default:
      return list.sort(
        (a, b) =>
          Number(a.totalPrice || a.netPrice || 0) -
          Number(b.totalPrice || b.netPrice || 0),
      );
  }
};

function EsimPlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = useMemo(
    () => parseEsimSearchQuery(searchParams),
    [searchParams],
  );

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [sort, setSort] = useState('price-asc');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [detailPlan, setDetailPlan] = useState(null);
  const [editSearchOpen, setEditSearchOpen] = useState(false);

  // Country missing → bounce back to landing.
  useEffect(() => {
    if (!search.country) {
      navigate(ESIM_ROUTES.LANDING, { replace: true });
    }
  }, [search.country, navigate]);

  // Fetch plans whenever the country in the URL changes.
  useEffect(() => {
    if (!search.country) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrorMsg('');
    setSelectedPlanId('');
    getEsimPlansApi({ country: search.country }).then(
      ({ ok, plans: list, message }) => {
        if (cancelled) return;
        if (!ok) {
          setErrorMsg(message || 'No plans available for this destination.');
          setPlans([]);
        } else {
          setPlans(list);
        }
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [search.country]);

  const filteredPlans = useMemo(() => {
    const filtered = plans.filter(
      (p) =>
        matchesDataBuckets(p, filters.dataBuckets) &&
        matchesValidityBuckets(p, filters.validityBuckets),
    );
    return sortPlans(filtered, sort);
  }, [plans, filters, sort]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.planId === selectedPlanId) || null,
    [plans, selectedPlanId],
  );

  const tripDays = tripDurationInDays(search.travelStart, search.travelEnd);

  const handleSelectPlan = (plan) => {
    if (!plan) return;
    setSelectedPlanId(plan.planId === selectedPlanId ? '' : plan.planId);
  };

  const handleViewDetails = (plan) => {
    setDetailPlan(plan);
  };

  const handleSearchAgain = (next) => {
    setEditSearchOpen(false);
    const qs = buildEsimSearchQuery(next);
    navigate(`${ESIM_ROUTES.PLANS}?${qs}`);
  };

  const handleProceed = () => {
    if (!selectedPlan) return;
    navigate(ESIM_ROUTES.BOOKING, {
      state: {
        plan: selectedPlan,
        search,
      },
    });
  };

  return (
    <div className="esim-plans-page">
      <section className="esim-plans-page__summary-bar">
        <Container>
          <div className="esim-plans-page__summary-inner">
            <div className="esim-plans-page__summary-meta">
              <span className="esim-plans-page__summary-label">
                <FiGlobe />
                Destination
              </span>
              <strong className="esim-plans-page__summary-value">
                {search.country || '—'}
              </strong>
            </div>
            <div className="esim-plans-page__summary-meta">
              <span className="esim-plans-page__summary-label">
                <FiCalendar />
                Travel Window
              </span>
              <strong className="esim-plans-page__summary-value">
                {search.travelStart || '—'}
                <span aria-hidden> → </span>
                {search.travelEnd || '—'}
                {tripDays > 0 && (
                  <small>
                    ({tripDays} {tripDays === 1 ? 'day' : 'days'})
                  </small>
                )}
              </strong>
            </div>
            <button
              type="button"
              className="esim-plans-page__edit-btn"
              onClick={() => setEditSearchOpen(true)}
            >
              <FiEdit2 />
              Modify Search
            </button>
          </div>
        </Container>
      </section>

      <Container className="esim-plans-page__main">
        <Row>
          <Col lg={3} md={4} className="esim-plans-page__sidebar">
            <EsimPlanFilters
              plans={plans}
              filters={filters}
              onChange={setFilters}
            />
            <div className="esim-plans-page__sidebar-spacer" />
            <EsimFareSummary
              plan={selectedPlan}
              currency={selectedPlan?.currency}
            />
          </Col>

          <Col lg={9} md={8}>
            <div className="esim-plans-page__toolbar">
              <div className="esim-plans-page__toolbar-results">
                {loading
                  ? 'Loading plans…'
                  : `${filteredPlans.length} plan${
                      filteredPlans.length === 1 ? '' : 's'
                    } available`}
              </div>
              <div
                className="esim-plans-page__sort-tabs"
                role="tablist"
                aria-label="Sort plans"
              >
                {ESIM_SORT_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={sort === tab.key}
                    className={`esim-plans-page__sort-tab ${
                      sort === tab.key
                        ? 'esim-plans-page__sort-tab--active'
                        : ''
                    }`}
                    onClick={() => setSort(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {errorMsg && (
              <div className="esim-plans-page__alert">
                <AppAlert
                  variant="warning"
                  showIcon
                  title="No plans found"
                  description={errorMsg}
                  autoHideTime={120000}
                  onHide={() => setErrorMsg('')}
                />
              </div>
            )}

            {loading ? (
              <div className="esim-plans-page__skeletons">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="esim-plans-page__skeleton" />
                ))}
              </div>
            ) : (
              <div className="esim-plans-page__list">
                {filteredPlans.length === 0 && !errorMsg && (
                  <div className="esim-plans-page__empty">
                    <h3>No plans match the chosen filters</h3>
                    <p>
                      Try clearing the filters or pick a different destination
                      to see more eSIM offerings.
                    </p>
                    <button
                      type="button"
                      className="esim-plans-page__empty-btn"
                      onClick={() => setFilters(EMPTY_FILTERS)}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
                {filteredPlans.map((plan) => (
                  <EsimPlanCard
                    key={plan.planId}
                    plan={plan}
                    selected={plan.planId === selectedPlanId}
                    onSelect={handleSelectPlan}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {selectedPlan && (
        <div className="esim-plans-page__sticky-bar">
          <Container className="esim-plans-page__sticky-inner">
            <div className="esim-plans-page__sticky-text">
              <strong>{selectedPlan?.genericProductName || 'eSIM Plan'}</strong>
              <span>selected — review traveller details to continue.</span>
            </div>
            <button
              type="button"
              className="esim-plans-page__sticky-btn"
              onClick={handleProceed}
            >
              Proceed to Add Traveller Details
              <FiArrowRight />
            </button>
          </Container>
        </div>
      )}

      <EsimPlanDetailsModal
        show={!!detailPlan}
        plan={detailPlan}
        onHide={() => setDetailPlan(null)}
        onSelect={(p) => handleSelectPlan(p)}
      />

      <Modal
        show={editSearchOpen}
        onHide={() => setEditSearchOpen(false)}
        size="lg"
        centered
        dialogClassName="esim-plans-page__edit-modal"
      >
        <Modal.Body>
          <button
            type="button"
            className="esim-plans-page__edit-close"
            onClick={() => setEditSearchOpen(false)}
            aria-label="Close"
          >
            <FiX />
          </button>
          <h3 className="esim-plans-page__edit-title">Modify your search</h3>
          <EsimSearchForm
            initialValues={search}
            onSearch={handleSearchAgain}
            compact
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default EsimPlansPage;
